import * as RadixDialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { CornerDownLeft, Languages, LogOut, Search } from "lucide-react";
import { useNavigate } from "react-router";

import { useSession } from "@/app/session/useSession";
import { useNavAllowed } from "@/app/shell/useNavAllowed";
import { NAV_ENTRIES, type NavEntry } from "@/app/shell/nav";
import { PaletteCreateItems, PaletteItem, PaletteThemeItems } from "@/app/shell/PaletteItems";
import { useProjects } from "@/features/projects/api";
import { ServiceTile } from "@/features/projects/components/ServiceIcon";
import { WorkspaceSearch } from "@/features/search/components/WorkspaceSearch";
import { paletteFilter, projectItemValue } from "@/features/search/lib/paletteFilter";
import { useLocale, useT } from "@/shared/i18n/useT";
import { Kbd } from "@/shared/ui/Kbd";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewProject: () => void;
  onNewTask: () => void;
}

const GROUP =
  "[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[12.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.02em] [&_[cmdk-group-heading]]:text-ink-subtle";

/** A "Go to" entry for one main section, matched by its label in either language. */
function PaletteNavItem({ entry, onSelect }: { entry: NavEntry; onSelect: () => void }) {
  const t = useT();
  const allowed = useNavAllowed(entry);
  if (!allowed) return null;
  const Icon = entry.icon;
  const label = t(entry.labelId);
  const keywords = [label, ...(entry.keywordIds?.map((id) => t(id)) ?? [])];
  return (
    <PaletteItem value={`go ${entry.id}`} keywords={keywords} onSelect={onSelect}>
      <Icon aria-hidden="true" />
      {label}
    </PaletteItem>
  );
}

export function CommandPalette({
  open,
  onOpenChange,
  onNewProject,
  onNewTask,
}: CommandPaletteProps) {
  const t = useT();
  const navigate = useNavigate();
  const { toggleLocale } = useLocale();
  const { signOut } = useSession();
  const projects = useProjects();

  const run = (action: () => void) => () => {
    onOpenChange(false);
    action();
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-60 animate-fade-in bg-scrim" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed start-1/2 top-[12vh] z-60 w-[min(620px,calc(100vw-1.5rem))] -translate-x-1/2 animate-pop-in overflow-hidden rounded-2xl border border-line bg-surface shadow-overlay rtl:translate-x-1/2"
        >
          <RadixDialog.Title className="sr-only">{t("palette.title")}</RadixDialog.Title>
          <Command loop filter={paletteFilter} className={GROUP}>
            <div className="flex items-center gap-3 border-b border-line px-4">
              <Search className="size-[18px] text-ink-subtle" aria-hidden="true" />
              <Command.Input
                placeholder={t("palette.placeholder")}
                className="h-14 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-subtle"
              />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="max-h-[min(56vh,460px)] scroll-py-2 overflow-y-auto p-2">
              <WorkspaceSearch
                onOpen={(target) => {
                  run(() => void navigate(target.to, { state: target.state }))();
                }}
                empty={
                  <Command.Empty className="px-3 py-10 text-center text-[14px] text-ink-muted">
                    {t("palette.empty")}
                  </Command.Empty>
                }
              />
              <Command.Group heading={t("palette.navigation")}>
                {NAV_ENTRIES.map((entry) => (
                  <PaletteNavItem
                    key={entry.id}
                    entry={entry}
                    onSelect={run(() => void navigate(entry.to))}
                  />
                ))}
              </Command.Group>
              {projects.data?.length ? (
                <Command.Group heading={t("palette.projects")}>
                  {projects.data.map((project) => (
                    <PaletteItem
                      key={project.id}
                      value={projectItemValue(project.key, project.name)}
                      onSelect={run(() => void navigate(`/projects/${project.key}`))}
                    >
                      <ServiceTile service={project.service} size="sm" />
                      <span className="user-text min-w-0 flex-1 truncate">{project.name}</span>
                      <span dir="ltr" className="font-mono text-[12px] text-ink-subtle">
                        {project.key}
                      </span>
                    </PaletteItem>
                  ))}
                </Command.Group>
              ) : null}
              <Command.Group heading={t("palette.actions")}>
                <PaletteCreateItems run={run} onNewProject={onNewProject} onNewTask={onNewTask} />
                <PaletteItem value="language" onSelect={run(toggleLocale)}>
                  <Languages aria-hidden="true" />
                  {t("palette.switchLanguage")}
                </PaletteItem>
                <PaletteThemeItems run={run} />
                <PaletteItem value="sign out" onSelect={run(() => void signOut())}>
                  <LogOut className="rtl:-scale-x-100" aria-hidden="true" />
                  {t("palette.signOut")}
                </PaletteItem>
              </Command.Group>
            </Command.List>
            <div className="flex items-center gap-4 border-t border-line bg-surface-subtle px-4 py-2.5 text-[12.5px] text-ink-subtle">
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                {t("palette.hintMove")}
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft className="size-3" aria-hidden="true" />
                </Kbd>
                {t("palette.hintOpen")}
              </span>
            </div>
          </Command>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
